import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from "react";
import { Scanner, ScannerData } from "./types";
import { appLog } from "./helpers";
import { verbose } from "./constants";
import { hasGrantedWebSerialAccess, WEB_SERIAL_SCANNER_FRIENDLY_NAME } from "./WebSerialScanner";
import { resolveScannerNavigationTarget } from "@/features/navigation/helpers";

// Declare global EB object
declare global {
  interface Window {
    EB?: {
      Barcode: {
        enumerate: () => Scanner[];
      };
      Application: {
        quit: () => void;
      };
      Notification?: {
        beep: (options: { volume: number; duration: number }) => void;
      };
    };
  }
}

interface ScannerContextProps {
  scanners: Scanner[];
  selectedScanner: Scanner | null;
  scannedData: string;
  pendingNavigationTarget: string | null;
  setScanners: (scanners: Scanner[]) => void;
  selectScanner: (scanner: Scanner) => void;
  disableScanner: () => void;
  enableScanner: (autoRead: boolean, message?: string, requestPermission?: boolean) => Promise<void>;
  startScanner: () => void;
  stopScanner: () => void;
  setScannedData: (data: string) => void;
  clearScannedData: () => void;
  clearPendingNavigationTarget: () => void;
  isWebSerialPermissionPending: boolean;
  requestWebSerialPermission: () => void;
  shouldShowWebSerialOverlay: boolean;
}

const ScannerContext = createContext<ScannerContextProps | undefined>(undefined);

export const useScannerContext = () => {
  const context = useContext(ScannerContext);
  if (!context) throw new Error("useScannerContext must be used within ScannerProvider");
  return context;
};

export const ScannerProvider = ({ children }: { children: ReactNode }) => {
  const [scanners, setScanners] = useState<Scanner[]>([]);
  const [selectedScanner, setSelectedScanner] = useState<Scanner | null>(null);
  const [scannedData, setScannedData] = useState<string>("");
  const [pendingNavigationTarget, setPendingNavigationTarget] = useState<string | null>(null);
  const [isWebSerialPermissionPending, setIsWebSerialPermissionPending] = useState(false);

  const isWebSerialSelected = selectedScanner?.friendlyName === WEB_SERIAL_SCANNER_FRIENDLY_NAME;

  // Expose test function to window for console testing
  useEffect(() => {
    (window as any).testScannerInput = (data: string) => {
      appLog(`[TEST_SCANNER_INPUT] ${data}`, true, "info");
      onScannerData({ data });
    };

    return () => {
      delete (window as any).testScannerInput;
    };
  }, []);

  // Data processing
  const onScannerData = (data: ScannerData) => {
    if (data.data) {
      const dataString = data.data;
      const navigationTarget = resolveScannerNavigationTarget(dataString);
      if (navigationTarget) {
        setPendingNavigationTarget(navigationTarget);
        setScannedData("");
        appLog(`[SCANNER_NAVIGATION] ${navigationTarget}`, true, "info", { dataString });
        return;
      }

      setScannedData(dataString);
      appLog(`[SCANNER_DATA] ${dataString}`, true, "info", { dataString: dataString });
    }
  };

  const selectScanner = useCallback((scanner: Scanner) => {
    setSelectedScanner(scanner);
    appLog(`[selectScannerByName] ${scanner.friendlyName}`, true);
  }, []);

  const disableScanner = () => {
    if (!selectedScanner?.disable) return;
    try {
      const result = selectedScanner.disable();
      if (result && typeof (result as Promise<void>).then === "function") {
        (result as Promise<void>).catch((error) =>
          appLog(`[ScannerContext] disableScanner error: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error)
        );
      }
    } catch (error) {
      appLog(`[ScannerContext] disableScanner threw: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error);
    }
  };

  const startScanner = () => {
    if (!selectedScanner?.start) return;
    try {
      const result = selectedScanner.start();
      if (result && typeof (result as Promise<void>).then === "function") {
        (result as Promise<void>).catch((error) =>
          appLog(`[ScannerContext] startScanner error: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error)
        );
      }
    } catch (error) {
      appLog(`[ScannerContext] startScanner threw: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error);
    }
  };

  const stopScanner = () => {
    if (!selectedScanner?.stop) return;
    try {
      const result = selectedScanner.stop();
      if (result && typeof (result as Promise<void>).then === "function") {
        (result as Promise<void>).catch((error) =>
          appLog(`[ScannerContext] stopScanner error: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error)
        );
      }
    } catch (error) {
      appLog(`[ScannerContext] stopScanner threw: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error);
    }
  };

  const enableScanner = useCallback(
    async (autoRead: boolean = true, message: string = "", requestPermission: boolean = false) => {
      if (selectedScanner?.enable) {
        appLog("[ScannerContext] enableScanner" + message);
        try {
          if (selectedScanner.friendlyName === WEB_SERIAL_SCANNER_FRIENDLY_NAME) {
            const hasPermission = await hasGrantedWebSerialAccess();
            if (!hasPermission && !requestPermission) {
              setIsWebSerialPermissionPending(true);
              appLog("[ScannerContext] Web Serial access not granted; showing permission overlay", true, "warn");
              return;
            }
            setIsWebSerialPermissionPending(requestPermission);
          }
          const result = selectedScanner.enable({ autoRead }, onScannerData);
          const isPromise = result && typeof (result as Promise<void>).then === "function";
          if (isPromise) {
            (result as Promise<void>).then(() => {
              appLog("[ScannerContext] enableScanner", true, "info", { data: "resolved" });
              setIsWebSerialPermissionPending(false);
            }).catch((error) => {
              appLog(`[ScannerContext] enableScanner error: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error);
              setIsWebSerialPermissionPending(false);
            });
          } else {
            appLog("[ScannerContext] enableScanner", true, "info", { data: result });
            setIsWebSerialPermissionPending(false);
          }
        } catch (error) {
          appLog(`[ScannerContext] enableScanner threw: ${(error as Error)?.message ?? "unknown"}`, true, "error", error as Error);
          setIsWebSerialPermissionPending(false);
        }
      } else if (selectedScanner !== null) {
        appLog(`[ScannerContext] enableScanner missing enable on ${selectedScanner?.friendlyName}` + message);
      }
    },
    [onScannerData, selectedScanner]
  );

  const requestWebSerialPermission = useCallback(() => {
    if (isWebSerialSelected) {
      void enableScanner(true, " [manual permission request]", true);
    }
  }, [enableScanner, isWebSerialSelected]);

  const shouldShowWebSerialOverlay = useMemo(() => {
    return isWebSerialSelected && isWebSerialPermissionPending;
  }, [isWebSerialPermissionPending, isWebSerialSelected]);

  useEffect(() => {
    let idx = 0;
    for (const scanner of scanners) {
      appLog(`[setScanners] ${idx} ${scanner.friendlyName}`, true);
      idx++;
    }
  }, [scanners]);

  const clearScannedData = () => {
    if (verbose) {
      console.log("CLR");
    }
    setScannedData("");
  };

  const clearPendingNavigationTarget = () => {
    setPendingNavigationTarget(null);
  };

  return (
    <ScannerContext.Provider
      value={{
        scanners,
        selectedScanner,
        setScanners,
        selectScanner,
        stopScanner,
        startScanner,
        disableScanner,
        enableScanner,
        scannedData,
        pendingNavigationTarget,
        setScannedData,
        clearScannedData,
        clearPendingNavigationTarget,
        isWebSerialPermissionPending,
        requestWebSerialPermission,
        shouldShowWebSerialOverlay,
      }}
    >
      {children}
      <WebSerialPermissionOverlay />
    </ScannerContext.Provider>
  );
};

export default ScannerProvider;

const overlayStyles = {
  backdrop: "fixed inset-0 z-50 flex items-center justify-center bg-black/75",
  panel: "bg-white text-black rounded-lg shadow-xl p-6 w-full max-w-sm text-center space-y-4",
  title: "text-lg font-semibold",
  button: "inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
};

const WebSerialPermissionOverlay = () => {
  const { shouldShowWebSerialOverlay, requestWebSerialPermission } = useScannerContext();

  if (!shouldShowWebSerialOverlay) {
    return null;
  }

  return (
    <div className={overlayStyles.backdrop} role="dialog" aria-modal="true">
      <div className={overlayStyles.panel}>
        <div className={overlayStyles.title}>Selecteer scanner</div>
        <p className="text-sm text-gray-700">
          Klik op onderstaande knop om de USB scanner te verbinden. Chrome vraagt om toestemming; kies vervolgens
          &quot;Symbol Bar Code Scanner&quot;.
        </p>
        <button className={overlayStyles.button} onClick={requestWebSerialPermission} type="button">
          Selecteer scanner
        </button>
      </div>
    </div>
  );
};
